import { PercentPipe } from '@angular/common';
import { Injectable, Renderer2 } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AttributeBase, Leg, ScheduleName } from '@approot/models';
import {
  AdminService,
  ConfigService,
  LegSyncService,
  PricingExtraService,
  ProductsConfigService
} from '@approot/services';
import {
  DateUtilities,
  Utilities,
  clone,
  getOppositePosition
} from '@approot/utilities';
import { lastDateToUseFn } from '@approot/utilities/schedule-utilities';
import { LegScheduleService } from '@approot/widgets/strategy-detail/leg/services/leg-schedule.service';
import { barrierTypeValDisablerEnabler } from '../utilities/barrier-utilities';
import { setCurrencyWithPreSetPosition } from '../utilities/currency-utilities';
import {
  getAttributeControlFromKey,
  getAttributeFromKey
} from '../utilities/leg-attribute-utilities';
import {
  postLoadingPresetsDcd,
  postLoadingPresetsTpf,
  setPercentageOnLoad,
  setPresetCutOff,
  setPresetFixingSource,
  setupFirstOfEnumDefault
} from '../utilities/leg-loading-utilities';
import {
  blurRounding,
  isDropdownOptionValid,
  loadSchedules,
  makeTextCustomColor,
  optionsChangeHandler,
  removeLastFixingIcon,
  setAllFieldsReadOnlyState,
  setAttributeReadOnly
} from '../utilities/leg.utilities';
import { LegCrossService } from './leg-cross.service';
import { LegDateService } from './leg-date.service';
import { LegElemService } from './leg-elem.service';
import { LegHelperService } from './leg-helper.service';
import { LegMiscellaneousService } from './leg-miscellaneous.service';
import { LegNotionalService } from './leg-notional.service';
import { LegSpotStrikeService } from './leg-spot-strike.service';

@Injectable({
  providedIn: 'root'
})
export class LegLoadingService {
  constructor(
    private legHelperService: LegHelperService,
    private legNotionalService: LegNotionalService,
    private pricingExtraService: PricingExtraService,
    private legElemService: LegElemService,
    private renderer: Renderer2,
    private productsConfigService: ProductsConfigService,
    private legDateService: LegDateService,
    private legCrossService: LegCrossService,
    private legSyncService: LegSyncService,
    private percentPipe: PercentPipe,
    private adminService: AdminService,
    private legSpotStrikeService: LegSpotStrikeService,
    private legMiscellaneousService: LegMiscellaneousService,
    private legScheduleService: LegScheduleService,
    private configService: ConfigService
  ) {}

  public preLoadingSetup(allAttributesBase: AttributeBase<any>[]): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    if (
      form.get('notional') &&
      form.get('counterNotional') &&
      !leg.preSetPosition?.faceCurrency
    ) {
      this.legNotionalService.handleNotionalSelectedRecords('', 'notional');
    }

    if (
      form.get('notional1') &&
      form.get('counterNotional1') &&
      !leg.preSetPosition?.faceCurrency1
    ) {
      this.legNotionalService.handleNotionalSelectedRecords('1', 'notional1');
    }

    if (
      form.get('notional2') &&
      form.get('counterNotional2') &&
      !leg.preSetPosition?.faceCurrency2
    ) {
      this.legNotionalService.handleNotionalSelectedRecords('2', 'notional2');
    }

    if (leg.productType === 'europeanbasketoption') {
      optionsChangeHandler(
        form,
        leg,
        'currency',
        this.pricingExtraService.currencies,
        ''
      );
    }

    allAttributesBase.forEach(attribute => {
      this.legElemService.handleNumberField(attribute, true);
      this.legHelperService.subSink.sink = form
        .get(attribute.key)
        .valueChanges.subscribe(val => {
          if (
            getOppositePosition(attribute.key) &&
            typeof val === 'string' &&
            val.includes('ch')
          ) {
            blurRounding(attribute.key, form, leg);
          }
        });
    });

    Object.keys(form.value).forEach((key: string) => {
      // loop through all keys that exist in the form, and list onto the blur so that the data can be reformatted
      this.decimalCurrencyOnBlurFormatter(
        key,
        Utilities.attributeBelongsToWhichCross(key)
      );
    });

    const dateShortName = lastDateToUseFn(form);
    if (form.get(dateShortName)) {
      // needs to be here as view is accessible here
      // remove the lastFixingDate calender icon
      removeLastFixingIcon(
        this.renderer,
        this.legHelperService.qs('input#' + dateShortName)
      );
    }

    if (form.get('position')) {
      this.legHelperService.subSink.sink = form
        .get('position')
        .valueChanges.subscribe((position: string) => {
          if (
            form.get('premiumPayer') &&
            this.legHelperService.presetsLoaded &&
            !rfqExecutionSettings.rfqLeg
          ) {
            form
              .get('premiumPayer')
              .setValue(
                position !== 'Sell' ? 'Counterparty A' : 'Counterparty B'
              );
          }
        });
    }
  }

  public loadLegPresets(legIndex: number): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    Object.keys(form.controls).forEach(keyName => {
      const presetValue = leg.preSetPosition[keyName];
      const keyFormControl = form.get(keyName);
      const isKeyNameFixingOrCut =
        keyName === 'fixingSource' ||
        keyName === 'cutOff' ||
        keyName === 'forcedCashSettlementFixingSource';

      const attribute = getAttributeFromKey(leg, keyName);

      const skipSettingValue =
        presetValue === undefined ||
        (((keyFormControl.value && attribute?.type !== 'multiSelect') ||
          (attribute?.type === 'multiSelect' && keyFormControl.value.length)) &&
          !(isKeyNameFixingOrCut || keyName === 'tradeId'));

      if (skipSettingValue) {
        return; // if attribute is undefined just leave it empty
      }
      // if the destination leg has hard-coded set values, do not override them
      // the fixingSource/ cutoff are set by the cross, we may want to override this
      if (
        (attribute?.type === 'enum' || // if the attribute is a dropdown, we need to check if the value is a valid option
          attribute?.type === 'multiSelect') && // if the attribute is a dropdown, we need to check if the value is a valid option
        !isKeyNameFixingOrCut &&
        !isDropdownOptionValid(keyName, presetValue, leg)
      ) {
        // if the value is not a valid option of the dropdown, leave it empty
        return;
      }

      switch (attribute.format) {
        case 'percentage':
          setPercentageOnLoad(keyName, form, leg, this.percentPipe); // margin should be a percentage
          break;
        default:
          this.setNonFormatPreset(keyName, presetValue, legIndex);
          break;
      }

      if (leg.preSetPosition[keyName] === Utilities.customText) {
        // when loading if the value is Custom, set the text color
        makeTextCustomColor(keyName, Utilities.orangeTextColor, leg);
      }
    });
  }

  public refreshAttributesOnLoad(): void {
    const form = this.legHelperService.form;
    const cross = form.get('cross')?.value;
    const depositCurrency = form.get('depositCurrency')?.value;
    const alternativeCurrency = form.get('alternativeCurrency')?.value;
    const crossToUse =
      cross ||
      (alternativeCurrency && depositCurrency
        ? this.productsConfigService.getCrossFromCurrencies(
            alternativeCurrency,
            depositCurrency
          )
        : null);
    if (!this.legHelperService.rfqExecutionSettings.rfqLeg && crossToUse) {
      this.legSpotStrikeService.spotCaller(crossToUse, 'spot', true);
      this.legSpotStrikeService.spotCaller(crossToUse, 'spotAsk', true, true);
    }
  }

  public restoreAttributesData(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    if (!leg.copiedAttributesData) {
      return;
    }
    Object.keys(leg.copiedAttributesData).forEach(key => {
      const attribute = getAttributeControlFromKey(
        key,
        leg.attributesAsFormControls
      );
      if (attribute && form.get(key).value) {
        Object.assign(attribute, {
          ...attribute,
          ...leg.copiedAttributesData[key]
        });
      }
    });
    delete leg.copiedAttributesData;
  }

  private setNonFormatPreset(
    keyName: string,
    presetValue: string,
    legIndex: number
  ): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const keyFormControl = this.legHelperService.form.get(keyName);
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const legSyncingInfo = this.legHelperService.legSyncingInfo;
    switch (keyName) {
      case 'productType':
      case 'schedule':
      case 'exerciseSchedule':
      case 'floatingLegSchedule':
      case 'fixedLegSchedule':
      case 'fixedLegSchedule1':
      case 'fixedLegSchedule2':
      case 'optionsPortfolioSpots':
        break;
      case 'alternativeCurrency':
        if (
          leg.productType === 'newdcd' &&
          !leg.preSetPosition['startDate'] &&
          leg.preSetPosition['depositCurrency'] &&
          leg.preSetPosition['alternativeCurrency']
        ) {
          // if there is the currencies set and no start date then
          // set the start date to be 0b

          const createdCross =
            this.productsConfigService.getCrossFromCurrencies(
              form.get('depositCurrency').value,
              form.get('alternativeCurrency').value
            );
          if (createdCross) {
            const customPayload = DateUtilities.dateOrLagToPayload(
              '0b',
              createdCross,
              this.pricingExtraService.getMarketDataTimeStamp,
              this.productsConfigService.getCutOffCode(
                form.get('cutOff')?.value
              ),
              this.productsConfigService.getFixingSourceCode(
                form.get('fixingSource')?.value
              )
            );
            this.legDateService.calculateExpirationSettlementDate(
              null,
              {
                formControl: form.get('startDate'),
                setFormControl: true,
                controlName: 'startDate',
                useDate: true
              },
              '',
              customPayload
            );
          }
        } else {
          keyFormControl.setValue(presetValue);
        }
        break;
      case 'position':
      case 'cross':
      case 'direction':
      case 'depositTaker':
      case 'depositCurrency':
        keyFormControl.setValue(presetValue);
        break;
      case 'notional':
      case 'notional1':
      case 'notional2':
      case 'secondNotional':
      case 'nearAmount':
      case 'farAmount':
      case 'midPv':
      case 'premium':
      case 'valuationAdjustment':
      case 'putAmount':
      case 'instalmentAmount':
      case 'capLossAmount':
      case 'notionalLong':
      case 'notionalShort':
      case 'callNotional':
      case 'putNotional':
      case 'fixedAmountLowerEkiZone':
      case 'fixedAmountUpperEkiZone':
        keyFormControl.setValue('' + presetValue);
        break; // notional is a number from the backend, but must be a string in component
      case 'settlementDate':
      case 'expiryDate':
      case 'nearDate':
      case 'farDate':
      case 'depositDate':
      case 'firstFixingDate':
      case 'firstExpirationDate':
      case 'firstSettlementDate':
      case 'firstExerciseDate':
      case 'lastFixingDate':
      case 'lastExpirationDate':
      case 'lastSettlementDate':
      case 'windowDate':
      case 'windowStartDate':
      case 'windowEndDate':
      case 'strikeFixingDate':
      case 'tradeDate':
      case 'premiumPaymentDate':
      case 'valuationAdjustmentAmortizationStartDate':
      case 'mtcDate':
      case 'valuationAdjustmentAmortizationEndDate':
      case 'startDate':
      case 'endDate':
      case 'swaptionExpiryDate':
      case 'swaptionSettlementDate':
      case 'redemptionDate':
      case 'swapStartDate':
      case 'swapEndDate':
        keyFormControl.patchValue(
          !!presetValue ? DateUtilities.dateToPrettyDate(presetValue) : null,
          { emitEvent: false }
        );
        break;
      case 'barrier':
      case 'secondBarrier':
      case 'lowerBarrier':
      case 'upperBarrier':
      case 'barrierType':
      case 'lowerBarrierType':
      case 'upperBarrierType':
      case 'lkoLowerBarrier':
      case 'lkoUpperBarrier':
        keyFormControl.setValue(presetValue, {
          emitEvent: presetValue === Utilities.customText ? false : true
        });
        break;
      case 'faceCurrency':
        this.legCrossService.setLoadedFaceCurrency();
        break;
      case 'faceCurrency1':
        this.legCrossService.setLoadedFaceCurrency('1');
        break;
      case 'faceCurrency2':
        this.legCrossService.setLoadedFaceCurrency('2');
        break;
      case 'premiumSettlementDate':
        keyFormControl.setValue(presetValue, { emitEvent: false });
        this.legSyncService.syncPremiumSettlementDate(legIndex, presetValue);
        break;
      case 'premiumValueType':
        let emitEvent = !leg.preSetPosition.premiumSettlementDate;
        this.legSyncService.syncPremiumType(legIndex, presetValue);
        keyFormControl.setValue(presetValue, {
          emitEvent
        });
        if (presetValue === 'Spot') {
          setAttributeReadOnly(
            'premiumSettlementDate',
            true,
            leg,
            form,
            rfqExecutionSettings
          );
        }
        break;
      case 'counterPartyA':
      case 'counterPartyB':
      case 'nbFixings':
      case 'nbSettlementDates':
      case 'nbExpiries':
      case 'nbFixingFirstSchedule':
      case 'exerciseFrequency':
      case 'nbExerciseDates':
      // case 'fixingHolidayCalendars':
      case 'frequency': // we don't want to emit values for these as if any of the other fields are Custom
        // e.g. notional and strike, it will force these to be wiped, when the new schedule is generated,
        // which can not have custom values.
        keyFormControl.setValue(presetValue, {
          emitEvent: false
        });
        break;
      case 'cutOff':
        // for PRESET values cutoff is passed as 'longName' when duplicating/copying
        // (but uses 'code' for trades/strategies) so we need to check both
        setPresetCutOff(
          presetValue,
          keyFormControl,
          this.productsConfigService.cutOffTimesAndFixingSources.cutOffs,
          leg.isLegCopied
        );
        break;
      case 'fixingSource':
      case 'forcedCashSettlementFixingSource':
        setPresetFixingSource(
          presetValue,
          keyFormControl,
          this.productsConfigService.cutOffTimesAndFixingSources.fixingSources,
          leg.isLegCopied
        );
        break;
      case 'margin':
        if (
          leg.preSetPosition['marginType'] === 'Deposit Currency' ||
          (leg.preSetPosition['marginStyle'] &&
            leg.preSetPosition['marginStyle'] !== 'Percent Base' &&
            leg.preSetPosition['marginStyle'] !== 'Percent Term')
        ) {
          // margin is a currency
          keyFormControl.setValue('' + presetValue);
        } else {
          setPercentageOnLoad(keyName, form, leg, this.percentPipe); // margin should be a percentage
        }
        break;
      case 'forwardPointsBid':
      case 'forwardPointsAsk':
        if (!legSyncingInfo?.forwardPoints) {
          keyFormControl.setValue(presetValue);
        }
        break;
      case 'nbGuaranteedSettlements':
      case 'isGuaranteedSettlementLeveraged':
        if (!keyFormControl.disabled) {
          keyFormControl.setValue(presetValue);
        }
        break;
      default:
        keyFormControl.setValue(presetValue);
        break;
    }
  }

  public postLoadingPresets(originalLeg: Leg): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;

    ['currency', 'capLossCurrency', 'fixedAmountCurrency'].forEach(key =>
      setCurrencyWithPreSetPosition(key, form, leg)
    );

    postLoadingPresetsTpf(form, leg);

    ['barrier', 'secondBarrier', 'upperBarrier', 'lowerBarrier'].forEach(
      key => {
        if (form.get(key) && form.get(key).value !== Utilities.customText) {
          form.get(key).setValue(leg.preSetPosition[key]);
          if (form.get(key).value === Utilities.customText) {
            // if it gets set as custom
            makeTextCustomColor(key, Utilities.orangeTextColor, leg);
          }
        }
      }
    );

    if (form.get('basket') && leg.preSetPosition['basket']) {
      (form.get('basket') as FormArray).clear();
      leg.preSetPosition['basket'].forEach(basket => {
        (form.get('basket') as FormArray).push(
          Utilities.generateBasket(basket)
        );
      });
    }

    this.postLoadingPresetsOptPortfolio(form, leg);

    // we want to populate counterNotional as this isn't saved on the backend

    this.setCounterNotionalOnLoad('', form);
    this.setCounterNotionalOnLoad('1', form);
    this.setCounterNotionalOnLoad('2', form);

    [
      'schedule',
      'fixedLegSchedule',
      'fixedLegSchedule1',
      'fixedLegSchedule2',
      'floatingLegSchedule',
      'exerciseSchedule'
    ].forEach((scheduleName: ScheduleName) => {
      let fieldNumber = '';
      if (scheduleName === 'fixedLegSchedule1') {
        fieldNumber = '1';
      } else if (scheduleName === 'fixedLegSchedule2') {
        fieldNumber = '2';
      }

      loadSchedules(
        scheduleName,
        form,
        leg,
        this.legHelperService.rfqExecutionSettings.rfqLeg,
        this.percentPipe,
        this.configService.userSettings(),
        fieldNumber
      );
      this.legScheduleService.setScheduleValidators(scheduleName);
    });

    setupFirstOfEnumDefault(
      leg,
      this.adminService.attributeDefaults,
      form,
      this.productsConfigService.productInfo.attributesDisplayList,
      this.adminService.useDefaults
    ); // set up the defaults for the dropdowns that do not have a preset value
    // this.setTradeCustomDates();

    if (rfqExecutionSettings.static) {
      setAllFieldsReadOnlyState(leg, form, rfqExecutionSettings, true);
    }

    this.legHelperService.onLegPresetLoadedFn();
    this.postLoadingPresetsSpotCross(form, leg);
    this.postLoadingPresetsValAdjDate(form, leg);
    postLoadingPresetsDcd(form, leg, this.legHelperService.tradingMode);
    this.updateAttributesWithTabVersion(originalLeg);
    this.legMiscellaneousService.fixingSourcesCutOffsFetcher(form, leg);
    this.legScheduleService.updateScheduleValidity('schedule');
    this.updateAllDisabledOnNone();
    this.legHelperService.presetsLoaded = true;
  }

  public updateAttributesWithTabVersion(originalLeg: Leg): void {
    /* when legs are loaded from the tab,
       we need to restore the previous attribute data e.g. textColor, title, info
    */
    const leg = this.legHelperService.leg;
    if (originalLeg.legAttributeDataFromTab) {
      const clonedOriginalLeg = clone(originalLeg);
      delete clonedOriginalLeg.attributesAsFormControls;
      delete clonedOriginalLeg.attributes;
      Object.assign(leg, { ...leg, ...clonedOriginalLeg });
      leg.legAttributeDataFromTab.forEach(attributeData => {
        delete attributeData.info;
        const ctrl = getAttributeControlFromKey(
          attributeData.key,
          leg.attributesAsFormControls
        );
        if (ctrl) {
          Object.assign(ctrl, {
            ...ctrl,
            ...attributeData
          });
        }
      });
    }
  }

  private setCounterNotionalOnLoad(
    notionalNumber: string,
    form: FormGroup
  ): void {
    if (
      form.get('counterNotional' + notionalNumber) &&
      !form.get('counterNotional' + notionalNumber).value
    ) {
      // the counterNotional needs to be rebuilt from strike and notional as its not saved
      this.legSpotStrikeService.setNotionalOnStrikeChange(notionalNumber);
    }
  }

  private setOptionsPortfolioSpots(spots: any[], form: FormGroup): void {
    // spots = e.g. [{EURUSD: 4.123}, {AUDCAD: 1.123}......]
    // set options portfolio spots
    spots.forEach(spot => {
      const key = Object.keys(spot)[0]; // this is the key of the field, e.g. EURUSD, USDGBP
      const control = new FormControl('', Validators.required);
      (form.get('optionsPortfolioSpots') as FormArray).push(
        new FormGroup({
          [key]: control
        })
      );
      this.legSpotStrikeService.spotCaller(key, control, true); // call to get latest spots
    });
  }

  private decimalCurrencyOnBlurFormatter(
    attribute: string,
    crossType: string
  ): void {
    let attributeElem = this.legHelperService.qs(`#${attribute}`);
    if (attributeElem) {
      attributeElem.addEventListener('blur', () =>
        this.crossDecimalCurrencyBlurFormatter(crossType, attribute)
      );
    }
  }

  private crossDecimalCurrencyBlurFormatter(
    crossType: string,
    id: string
  ): void {
    const form = this.legHelperService.form;
    if (form.get(crossType) && form.get(crossType).value) {
      this.legHelperService.decimalCurrencyFormatter(id, crossType);
    }
  }

  private postLoadingPresetsValAdjDate(form: FormGroup, leg: Leg): void {
    if (
      form.get('valuationAdjustmentAmortizationStartDate') &&
      form.get('valuationAdjustmentAmortizationStartDate').enabled &&
      !form.get('valuationAdjustmentAmortizationStartDate').value &&
      this.adminService.useDefaults
    ) {
      const todayDate = DateUtilities.dateToPrettyDate(
        DateUtilities.getDateInISOFormat()
      );
      if (this.legHelperService.presetsLoaded) {
        form
          .get('valuationAdjustmentAmortizationStartDate')
          .setValue(todayDate);

        this.legHelperService.setDateOnDelay(
          'valuationAdjustmentAmortizationStartDate',
          todayDate
        );
      }
    }
  }

  private postLoadingPresetsSpotCross(form: FormGroup, leg: Leg): void {
    if (
      leg.productType === 'spot' &&
      form.get('cross') &&
      form.get('cross').value &&
      !form.get('settlementDate')
    ) {
      this.legDateService.defaultNextBusinessDay('settlementDate');
    }
    if (form.get('tradeId') && leg.preSetPosition['tradeId']) {
      makeTextCustomColor('tradeId', Utilities.greenTextColor, leg);
    }

    if (
      ((form.get('cross') && form.get('cross').value) ||
        (form.get('currency') && form.get('currency').value)) &&
      !leg.preSetPosition['premiumPaymentDate']
    ) {
      this.legDateService.setPremiumDefaultDate();
    }
  }

  private postLoadingPresetsOptPortfolio(form: FormGroup, leg: Leg): void {
    if (
      form.get('optionsPortfolio') &&
      leg.preSetPosition['optionsPortfolio']
    ) {
      (form.get('optionsPortfolio') as FormArray).clear();
      leg.preSetPosition['optionsPortfolio'].forEach(option => {
        (form.get('optionsPortfolio') as FormArray).push(
          Utilities.generateOption(option)
        );
      });
    }

    if (
      form.get('optionsPortfolioSpots') &&
      leg.preSetPosition['optionsPortfolioSpots']
    ) {
      // once everything has been loaded attempt to load the spots
      this.setOptionsPortfolioSpots(
        leg.preSetPosition['optionsPortfolioSpots'],
        form
      );
    }
  }

  private updateAllDisabledOnNone(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const attributes = this.productsConfigService.getAllAttributes(
      this.legHelperService.leg.attributesAsFormControls
    ) as AttributeBase<any>[];
    attributes.forEach(attribute => {
      attribute.attributesDisabledOnNone?.forEach(attr =>
        barrierTypeValDisablerEnabler(
          attribute.key,
          attr,
          form.get(attribute.key).value,
          form,
          leg
        )
      );
    });
  }
}
